import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../../../components/molecules/Form';
import FormsRow, { FormsRowModifier } from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import { ContentParagraph } from '../../../../_lib/components/ContentParagraph';
import { SearchBreadcrumb } from '../../../../components/molecules/SearchBreadcrumb';
import { Namespace } from '../../../../translation/namespace';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { ContentLink } from '../../../../_lib/components/ContentLink';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import useRedirect from '../../../../hooks/useRedirect';
import ModalContext from '../../../../components/organisms/ModalDialog/ModalContext';
import { ListBoxModifier, ContentSelectComboBox } from '../../../../components/molecules/Form/components/ComboBox';
import { StatusInfoError } from '../../../../_lib/components/StatusInfo';
import { FormSubmitButton } from '../../../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';

// valid 'errorType' in response from backend found und PageMoveService.
const ERROR_TYPE = {
  // if the search and its index are working fine only these should be visible for the user
  MISSING_PRIVILIGES_NEW_PARENT_PAGE: 'pageMoveMissingPrivilegesForNewParentPage',
  INVALID_PAGETYPE_OF_NEW_PARENT: 'pageMoveInvalidPageTypeOfNewParent',
  // search down or index corrupted (e.g local) -> these can occure.
  INVALID_PAGETYPE_OF_MOVED_PARENT: 'pageMoveInvalidPageTypeOfMovedPage',
  INVALID_PAGE_NEW_PARENT: 'pageMoveInvalidPageForNewParent',
  MOVE_TO_OWN_CHILD: 'pageMoveCanNotMoveToOwnChildPages',
  PAGEMOVE_INVALID_VERSIONSTATE_OF_NEW_PARENT: 'pageMoveInvalidVersionStateOfNewParent',
};

function PageMoveFormTemplate() {
  const { inputs, onChange, status, getStatus, response } = useFormContext();
  const { t } = useTranslation(Namespace.PageManagement);
  const location = useLocation();
  const [startRedirect] = useRedirect();
  const [selectedPage, setSelectedPage] = useState();
  const { preventModalClose, closePreventedMessage } = useContext(ModalContext);
  const { newParentPageId } = inputs;

  const getForwardMessage = () => {
    return translatePartOfStringWithLink(
      `${Namespace.PageManagement}:views.navigationsettings-move.form.status.successForward`,
      location.pathname,
      false,
    );
  };

  const getErrorMessage = key =>
    FormUtils.createStatusMessage(t(`views.navigationsettings-move.form.status.${key}`), InteractionStatus.FAIL);

  const statusmessageWithResponse = {
    [InteractionStatus.SUCCESS]: [
      FormUtils.createStatusMessage(
        t(`${Namespace.PageManagement}:views.navigationsettings-move.form.status.success`),
        InteractionStatus.SUCCESS,
      ),
      FormUtils.createStatusMessage(getForwardMessage(), InteractionStatus.SENDING),
    ],
    [InteractionStatus.FAIL]: getErrorMessage('fail'),
    [FormResponseErrorType.PAGE_LOCKED]: FormUtils.createStatusMessage(
      translatePartOfStringWithLink('phrases:pageLocked', response?.data?.pageLockedBy?.url, false, {
        name: response?.data?.pageLockedBy?.name,
      }),
    ),
    [ERROR_TYPE.INVALID_PAGETYPE_OF_MOVED_PARENT]: getErrorMessage(ERROR_TYPE.INVALID_PAGETYPE_OF_MOVED_PARENT),
    [ERROR_TYPE.INVALID_PAGETYPE_OF_NEW_PARENT]: getErrorMessage(ERROR_TYPE.INVALID_PAGETYPE_OF_NEW_PARENT),
    [ERROR_TYPE.INVALID_PAGE_NEW_PARENT]: getErrorMessage(ERROR_TYPE.INVALID_PAGE_NEW_PARENT),
    [ERROR_TYPE.MISSING_PRIVILIGES_NEW_PARENT_PAGE]: getErrorMessage(ERROR_TYPE.MISSING_PRIVILIGES_NEW_PARENT_PAGE),
    [ERROR_TYPE.MOVE_TO_OWN_CHILD]: getErrorMessage(ERROR_TYPE.MOVE_TO_OWN_CHILD),
    [ERROR_TYPE.PAGEMOVE_INVALID_VERSIONSTATE_OF_NEW_PARENT]: getErrorMessage(
      ERROR_TYPE.PAGEMOVE_INVALID_VERSIONSTATE_OF_NEW_PARENT,
    ),
  };

  useEffect(() => {
    if (status === InteractionStatus.SENDING) {
      preventModalClose(true);
    }

    if (status === InteractionStatus.SUCCESS) {
      startRedirect(location.pathname);
    }

    if (status === InteractionStatus.FAIL) {
      preventModalClose(false);
    }
  }, [location, preventModalClose, startRedirect, status]);

  const isSubmitButtonEnabled = () =>
    !!selectedPage && ![InteractionStatus.SUCCESS, InteractionStatus.SENDING].includes(status);

  return (
    <>
      <FormsRow>
        <ContentParagraph>{t('views.navigationsettings-move.form.hint')}</ContentParagraph>
      </FormsRow>
      <FormsRow>
        <ContentSelectComboBox
          id="combobox-pagemove"
          name={newParentPageId.name}
          hideLabelAddition
          modifierListBox={ListBoxModifier.RELATIVE} // listbox expands modal dialog
          suggestUrl={newParentPageId.comboBoxUrl.url}
          ariaAutoComplete="list"
          labelTitle={t('views.navigationsettings-move.form.newParentPage.label')}
          labelHelpText={t('views.navigationsettings-move.form.newParentPage.labelHelpText')}
          onSelection={(item) => {
            setSelectedPage(item);
            onChange(newParentPageId.name, item.id);
          }}
        />
      </FormsRow>
      {selectedPage && (
        <FormsRow modifier={FormsRowModifier.BLOCK}>
          <h3 className="form__headline">{t('views.navigationsettings-move.form.selectedPage')}</h3>
          <ContentLink to={selectedPage.pageLink.url}>{selectedPage.title}</ContentLink>
          {selectedPage.breadcrumb && <SearchBreadcrumb clickable breadcrumbs={selectedPage.breadcrumb} />}
        </FormsRow>
      )}
      <FormSubmitButton
        buttonLabel={t('views.navigationsettings-move.form.saveButtonLabel')}
        enabled={isSubmitButtonEnabled()}
      />
      <LiveRegion invisible={false} htmlTag="div">
        {getStatus(statusmessageWithResponse)}
        {closePreventedMessage && <StatusInfoError message={closePreventedMessage} modifier="right" />}
      </LiveRegion>
    </>
  );
}

export default PageMoveFormTemplate;
